import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone } from "lucide-react";
import { SERVICES, SERVICE_IMAGES, COMPANY } from "../data/site";
import { useReveal } from "../hooks/use-reveal";
import { useSeo } from "../hooks/use-seo";
import { ContactForm } from "../components/sections/ContactForm";

export default function ServicesIndex() {
    useReveal();
    useSeo({
        title: "Insulation Services | Spray Foam, Blown-In, Attic & More · HAZ Insulation",
        description:
            "Every insulation service HAZ offers — spray foam, blown-in cellulose, fiberglass batts, attic systems, crawl space encapsulation, dense pack, air sealing, removal & more.",
    });

    return (
        <main data-testid="services-index" className="pt-24">
            <section className="relative py-24 md:py-32 bg-ink text-bone overflow-hidden">
                <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-moss/40 blur-3xl" />
                <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5 reveal">
                        <span className="h-px w-10 bg-ember inline-block align-middle mr-3" />
                        Services
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[0.98] max-w-4xl reveal">
                        Fifteen services. <span className="text-ember">One crew that actually shows up.</span>
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-bone/85 max-w-3xl leading-relaxed reveal">
                        From flagship spray foam to dense-pack walls, attic systems to crawl space
                        encapsulation, we handle the entire building envelope — not just the part
                        everyone else wants to bid on.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal">
                        <Link
                            to="/contact"
                            className="btn-lift inline-flex items-center justify-center gap-2 bg-copper text-bone font-semibold py-4 px-8 hover:bg-copper-hover"
                        >
                            Get a Free Quote <ArrowRight size={18} />
                        </Link>
                        <a
                            href={COMPANY.phoneHref}
                            className="btn-lift inline-flex items-center justify-center gap-2 border-2 border-bone text-bone font-semibold py-4 px-8 hover:bg-bone hover:text-ink"
                        >
                            <Phone size={16} /> {COMPANY.phone}
                        </a>
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
                        {SERVICES.map((s, idx) => {
                            const isFlagship = !!s.kicker;
                            const photo = SERVICE_IMAGES[s.id];
                            const span = isFlagship ? "lg:col-span-6" : "lg:col-span-4";
                            return (
                                <Link
                                    key={s.id}
                                    to={`/services/${s.id}`}
                                    data-testid={`services-index-card-${s.id}`}
                                    className={`reveal group bg-white border border-line p-7 md:p-9 flex flex-col justify-between min-h-[260px] hover:-translate-y-1 transition-transform duration-300 relative overflow-hidden ${span}`}
                                    style={{ transitionDelay: `${(idx % 6) * 50}ms` }}
                                >
                                    {photo && (
                                        <>
                                            <img
                                                src={photo}
                                                alt={s.title}
                                                loading="lazy"
                                                className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-95 transition-opacity duration-500"
                                                style={{ filter: "brightness(1.15) contrast(1.05)" }}
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/75 to-white/40 group-hover:from-white/90 group-hover:via-white/55 group-hover:to-white/15 transition-all duration-500" />
                                            {isFlagship && (
                                                <div className="absolute top-5 right-5 z-20 text-[10px] tracking-[0.2em] uppercase font-bold text-copper bg-bone/90 px-2 py-1">
                                                    {s.kicker}
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <div className="relative z-10">
                                        <div className="flex items-start justify-between gap-4">
                                            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
                                                {s.title}
                                            </h2>
                                            <ArrowUpRight
                                                size={22}
                                                strokeWidth={1.5}
                                                className="text-ink opacity-50 group-hover:opacity-100 group-hover:text-copper group-hover:rotate-12 transition-all shrink-0"
                                            />
                                        </div>
                                        <p className="mt-3 text-sm md:text-base text-ink/80 leading-relaxed font-medium">
                                            {s.blurb}
                                        </p>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            <ContactForm />
        </main>
    );
}
